import React, { useMemo, useState } from 'react';
import L from 'leaflet';
import { GeoJSON, Marker, Tooltip } from 'react-leaflet';
import { Info } from '@phosphor-icons/react';
import GenericMap from '../../../shared/components/common/GenericMap';
import { buildMarkerIcon } from '../../tasks/utils/taskDistributionUtils';
import MapBounds from './MapBounds';
import ConfirmedRouteTaskMarker from './ConfirmedRouteTaskMarker';
import { getDataPalette } from '../../../styles/dataColors.js';

// Tono más oscuro para el borde del marcador, derivado del color de dato en
// vez de un segundo token: son 6 categorías, no 12 (Regla de Tres).
function darken(rgbString, amount = 0.28) {
    const match = rgbString.match(/rgb\((\d+) (\d+) (\d+)\)/);
    if (!match) return rgbString;
    const [r, g, b] = match.slice(1).map(Number);
    const shade = (c) => Math.max(0, Math.round(c * (1 - amount)));
    return `rgb(${shade(r)}, ${shade(g)}, ${shade(b)})`;
}

function TaskMap({
    tasks,
    routePlans = [],
    defaultCenter = { lat: -31.5375, lng: -68.5364 }
}) {
    const [showTooltip, setShowTooltip] = useState(false);
    const baseIcon = useMemo(() => buildMarkerIcon('B', '#ffffff', { isBase: true }), []);
    const maxBounds = useMemo(
        () => L.latLng(defaultCenter.lat, defaultCenter.lng).toBounds(120000),
        [defaultCenter.lat, defaultCenter.lng]
    );
    const hasOutdatedRoutePlans = routePlans.some((plan) => plan?.isLatest === false);
    const isRecalculatingRoute = routePlans.some((plan) => (
        plan?.recalculationStatus === 'pending' || plan?.recalculationStatus === 'running'
    ));
    const routeRecalculationFailed = routePlans.find((plan) => plan?.recalculationStatus === 'failed');
    const colors = getDataPalette().map((fill) => ({ fill, stroke: darken(fill) }));

    const hasValidCoordinates = (lat, lng) => (
        lat != null && lng != null && String(lat).trim() !== '' && String(lng).trim() !== ''
        && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))
    );

    const normalizedRoutePlans = routePlans
        .filter(Boolean)
        .map((plan, index) => ({
            ...plan,
            routeColor: plan.color || colors[index % colors.length].stroke
        }));

    const routeTaskMarkers = normalizedRoutePlans.flatMap((plan, planIndex) => {
        const seenTaskIds = new Set();
        let nextTaskNumber = 0;
        const uniqueTaskStops = (plan.stops || [])
            .filter((stop) => ['TASK', 'ROUTE_ORIGIN'].includes(stop.type) && stop.taskId && hasValidCoordinates(stop.lat, stop.lng))
            .sort((left, right) => Number(left.order || 0) - Number(right.order || 0))
            .filter((stop) => {
                const taskId = String(stop.taskId);
                if (seenTaskIds.has(taskId)) {
                    return false;
                }

                seenTaskIds.add(taskId);
                return true;
            });

        return uniqueTaskStops.map((stop) => {
            const isRouteOrigin = stop.type === 'ROUTE_ORIGIN' || stop.status === 'finalizada';
            if (!isRouteOrigin) {
                nextTaskNumber += 1;
            }

            return {
                id: `${plan.teamId}-${stop.taskId}`,
                lat: Number(stop.lat),
                lng: Number(stop.lng),
                taskTitle: stop.title || stop.name,
                number: isRouteOrigin ? null : nextTaskNumber,
                isRouteOrigin,
                color: plan.routeColor,
                teamName: plan.teamName || `Equipo ${planIndex + 1}`
            };
        });
    });

    // La base de regreso sigue presente aunque el origen ya sea una tarea.
    const routeBaseMarkers = [...new Map(normalizedRoutePlans.flatMap((plan) => (
        (plan.stops || [])
            .filter((stop) => ['BASE_START', 'BASE_END'].includes(stop.type) && hasValidCoordinates(stop.lat, stop.lng))
            .map((stop) => {
                const id = `base-${Number(stop.lat)}-${Number(stop.lng)}`;
                return [id, { id, lat: Number(stop.lat), lng: Number(stop.lng) }];
            })
    ))).values()];

    const fallbackTaskMarkers = tasks
        .filter((task) => task.status !== 'finalizada' && task.status !== 'cancelada')
        .filter((task) => task.client?.fakeLat && task.client?.fakeLng)
        .map((task) => ({
            id: task.id,
            lat: task.client.fakeLat,
            lng: task.client.fakeLng,
            taskTitle: task.title,
            number: task.number,
            color: null,
            teamName: task.team?.name || null
        }));

    const hasConfirmedRoute = normalizedRoutePlans.length > 0;
    const visibleTaskMarkers = hasConfirmedRoute ? routeTaskMarkers : fallbackTaskMarkers;

    const getNumberIcon = (number) => L.divIcon({
        className: 'task-number-icon',
        html: `<div style="display:flex;align-items:center;justify-content:center;width:24px;height:24px;border-radius:9999px;background:rgba(17,24,39,0.85);border:1px solid rgba(209,213,219,0.6);color:#fff;font-weight:700;font-size:12px;line-height:1;">${number}</div>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12]
    });

    const boundsMarkers = [...visibleTaskMarkers, ...routeBaseMarkers].map((marker) => ({
        lat: marker.lat,
        lng: marker.lng
    }));

    const routeBounds = normalizedRoutePlans.flatMap((plan) => (
        (plan.route?.geometry?.coordinates || []).map(([lng, lat]) => [lat, lng])
    ));
    const fitPoints = routeBounds.length > 0
        ? [...routeBounds, ...boundsMarkers.map((marker) => [marker.lat, marker.lng])]
        : [];
    const tooltipLabel = hasConfirmedRoute
        ? 'Línea = recorrido confirmado. B = base operativa.'
        : 'Radio (300m)';

    return (
        <div className="bg-surface rounded-xl shadow-md border border-line p-3 sm:p-4 lg:p-6 mb-4 sm:mb-6 relative z-0 mt-4 sm:mt-6">
            <div className="mb-4">
                <h3 className="text-base sm:text-lg font-semibold text-content mb-2">Mapa de Zonas de Trabajo</h3>
                <p className="text-xs sm:text-sm text-content-muted">
                    {hasConfirmedRoute
                        ? 'Se muestra el recorrido confirmado del día para el equipo'
                        : 'Haz clic en cualquier zona para ver los detalles de la tarea. Cada color representa una tarea diferente con un radio de 300 metros.'}
                </p>
            </div>

            <div className="relative">
                <GenericMap
                    center={defaultCenter}
                    zoom={12}
                    maxBounds={maxBounds}
                    maxBoundsViscosity={1.0}
                    style={{ height: '400px', width: '100%', borderRadius: '8px' }}
                    markers={hasConfirmedRoute ? [] : fallbackTaskMarkers.map((marker, index) => {
                        const color = marker.color
                            ? { fill: marker.color, stroke: marker.color }
                            : colors[index % colors.length];

                        return {
                            id: marker.id,
                            position: [marker.lat, marker.lng],
                            icon: getNumberIcon(marker.number),
                            interactive: false,
                            circle: {
                                radius: 300,
                                fillColor: color.fill,
                                color: color.stroke,
                                fillOpacity: 0.4,
                                weight: 3
                            }
                        };
                    })}
                >
                    {normalizedRoutePlans.filter((plan) => plan.route?.geometry).map((plan) => (
                        <GeoJSON
                            key={`route-${plan.teamId}-${JSON.stringify(plan.route.geometry)}`}
                            data={plan.route.geometry}
                            style={() => ({
                                color: plan.routeColor,
                                weight: 5,
                                opacity: 0.95
                            })}
                        />
                    ))}
                    {hasConfirmedRoute && routeTaskMarkers.map((marker) => (
                        <ConfirmedRouteTaskMarker key={marker.id} marker={marker} />
                    ))}
                    {routeBaseMarkers.map((marker) => (
                        <Marker
                            key={marker.id}
                            position={[marker.lat, marker.lng]}
                            icon={baseIcon}
                        >
                            <Tooltip direction="top" offset={[0, -8]}>Base operativa</Tooltip>
                        </Marker>
                    ))}
                    <MapBounds taskMarkers={boundsMarkers} fitPoints={fitPoints} maxBounds={maxBounds} />
                </GenericMap>

                {(hasOutdatedRoutePlans || routeRecalculationFailed) && (
                    <div className="pointer-events-none absolute inset-x-4 top-4 z-[1000] flex justify-center">
                        <div className="rounded-2xl border border-warning/40 bg-white/95 px-4 py-3 text-center text-sm font-semibold text-ink shadow-[0_16px_40px_rgba(15,23,42,0.45)] backdrop-blur-md">
                            {isRecalculatingRoute
                                ? 'Actualizando el recorrido desde la última tarea atendida...'
                                : routeRecalculationFailed
                                    ? 'No se pudo dibujar el recorrido actualizado. Las paradas se mantienen visibles.'
                                    : 'El recorrido tiene cambios pendientes de actualizar.'}
                        </div>
                    </div>
                )}

                <div
                    className="absolute top-3 right-3 z-[1000] flex flex-col items-end"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                >
                    <div className="bg-surface/80 hover:bg-surface-2 backdrop-blur-sm border border-line p-2 rounded-full shadow-lg transition-colors">
                        <Info size={20} weight="bold" className="text-content-muted hover:text-content" />
                    </div>

                    {showTooltip && (
                        <div className="mt-2 flex items-center bg-surface/90 backdrop-blur-sm border border-line px-3 py-2 rounded-lg shadow-xl animate-fade-in">
                            <div className="w-3 h-3 bg-info rounded-full opacity-60 border-[1.5px] border-info mr-2"></div>
                            <span className="text-content-muted text-xs font-medium">{tooltipLabel}</span>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-4 text-sm text-content-muted">
                {visibleTaskMarkers.length > 0 && (
                    <div className="pt-2">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                            <h5 className="font-medium text-content text-center sm:text-left">
                                {hasConfirmedRoute ? 'Paradas del recorrido' : 'Tareas Asignadas'}
                            </h5>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-1">
                            {visibleTaskMarkers.map((marker, index) => {
                                const color = marker.color
                                    ? { fill: marker.color }
                                    : colors[index % colors.length];

                                return (
                                    <div key={marker.id} className="flex items-center space-x-2 text-xs">
                                        <div
                                            className={`${marker.isRouteOrigin ? 'h-3 w-3' : 'h-5 w-5'} rounded-full border border-line flex items-center justify-center text-[16px] font-semibold text-ink`}
                                            style={{ backgroundColor: color.fill }}
                                        >
                                            {marker.isRouteOrigin ? null : marker.number}
                                        </div>
                                        <span className="truncate text-content text-xs">
                                            {marker.taskTitle}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TaskMap;
